/**
 * Estado da instalação do app (PWA), compartilhado pela faixa de convite e pelo
 * botão fixo do menu.
 *
 * **Instalar sozinho não existe:** nenhum navegador deixa um site se instalar
 * sem gesto de quem está usando. O que existe é o navegador entregar o convite
 * (`beforeinstallprompt`), o site guardar, e disparar a partir de um clique.
 *
 * Três caminhos, porque os navegadores não concordam:
 *
 *  1. **Android/Chrome e Edge** — o evento chega; `instalar()` abre o diálogo
 *     do sistema. Guardar o evento é obrigatório: ele vale **uma vez só** e só
 *     a partir de um gesto — chamar `prompt()` fora do clique não faz nada.
 *  2. **Windows (Chrome e Edge)** — é o MESMO evento: instala com ícone na área
 *     de trabalho e janela sem abas.
 *  3. **iPhone/iPad** — a Apple não implementa o evento. O único caminho é
 *     Compartilhar → Adicionar à Tela de Início, então resta instruir.
 *
 * O registro do service worker mora aqui porque é ele que torna o app
 * instalável — sem service worker com handler de `fetch`, o Chrome nunca
 * dispara o convite. Falha no registro não quebra nada: o sistema funciona
 * igual, só não aparece a opção de instalar.
 */
import { useCallback, useEffect, useState } from 'react';

/** O evento do Chrome; não está no padrão, então o TypeScript não tem tipo. */
interface EventoDeInstalacao extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type MeioDeInstalacao =
  /** O navegador ofereceu: dá para abrir o diálogo do sistema. */
  | 'prompt'
  /** iPhone/iPad: só instruindo pelo menu Compartilhar. */
  | 'ios'
  /** Já instalado, ou navegador que não instala (Firefox no desktop). */
  | 'indisponivel';

export function jaInstalado(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS antigo não implementa `display-mode` no matchMedia; lá é este.
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function eIos(): boolean {
  // `MSStream` exclui o Windows Phone antigo, que também dizia "like iPhone".
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !('MSStream' in window);
}

/** Só muda a PALAVRA do texto ("celular" ou "computador"), nunca o comportamento. */
export function noComputador(): boolean {
  return window.matchMedia('(pointer: fine) and (min-width: 1024px)').matches;
}

export interface EstadoDeInstalacao {
  meio: MeioDeInstalacao;
  /** Abre o diálogo do sistema. Só faz efeito quando `meio === 'prompt'`. */
  instalar: () => Promise<'accepted' | 'dismissed' | 'indisponivel'>;
}

export function useInstalacao(): EstadoDeInstalacao {
  const [evento, setEvento] = useState<EventoDeInstalacao | null>(null);
  const [instalado, setInstalado] = useState(() => jaInstalado());

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {
        /* navegador sem suporte, aba anônima, política do dispositivo */
      });
    }

    const capturar = (e: Event) => {
      // Sem o `preventDefault`, o Chrome mostra a barrinha dele por cima.
      e.preventDefault();
      setEvento(e as EventoDeInstalacao);
    };
    // Instalou por outro caminho (menu do navegador): o botão some.
    const instalou = () => {
      setEvento(null);
      setInstalado(true);
    };
    window.addEventListener('beforeinstallprompt', capturar);
    window.addEventListener('appinstalled', instalou);
    return () => {
      window.removeEventListener('beforeinstallprompt', capturar);
      window.removeEventListener('appinstalled', instalou);
    };
  }, []);

  const instalar = useCallback(async () => {
    if (evento === null) return 'indisponivel' as const;
    await evento.prompt();
    const { outcome } = await evento.userChoice;
    // O evento é de uso único: mesmo aceitando, não serve de novo.
    setEvento(null);
    return outcome;
  }, [evento]);

  const meio: MeioDeInstalacao = instalado
    ? 'indisponivel'
    : evento !== null
      ? 'prompt'
      : eIos()
        ? 'ios'
        : 'indisponivel';

  return { meio, instalar };
}
